import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Typography, Input, Button, Card, Form, Switch, Divider, Tag,
  Select, Alert, Spin, Space,
} from "antd";
import { ArrowLeftOutlined, UserAddOutlined } from "@ant-design/icons";
import { api } from "../../services/api";
import { PasarelasTienda } from "./PasarelasTienda";

const { Title, Text } = Typography;

const TwoCol = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>{children}</div>
);

const ThreeCol = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>{children}</div>
);

const ERP_TYPES = [
  { value: "ecommerce", label: "Catálogo propio (sin ERP)" },
  { value: "ip_erp_api", label: "iPerp (API)" },
  { value: "legacy_db", label: "Legacy (BD propia)" },
];

const EMPTY = {
  slug: "", nombre: "", display_name: "", descripcion: "", activa: true,
  erp_type: "ecommerce", erp_db_name: "", erp_api_key: "", webhook_secret: "",
  serpapi_key: "",
  color_primary: "", color_secondary: "", color_accent: "",
  color_background: "", color_text: "", color_primary_text: "",
  email: "", telefono: "", whatsapp: "", domicilio: "",
  website_url: "", instagram: "", facebook: "",
  custom_domain: "",
};

export const TiendaForm = () => {
  const { slug } = useParams();
  const isEdit = !!slug;
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ email: "", nombre: "", apellido: "", telefono: "" });
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [adminMsg, setAdminMsg] = useState({ type: "", text: "" });

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await api.platform.tiendas.get(slug);
        if (res.status === "success") {
          setForm({ ...EMPTY, ...res.data, activa: !!res.data.activa });
        } else {
          setError(res.message || "No se pudo cargar la tienda.");
        }
      } catch {
        setError("Error de red o servidor.");
      }
      setLoading(false);
    })();
  }, [slug, isEdit]);

  const cargarAdmins = async () => {
    const res = await api.platform.tiendas.listAdmins(slug);
    if (res.status === "success") setAdmins(res.data || []);
  };

  useEffect(() => {
    if (isEdit) cargarAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isEdit]);

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setAddingAdmin(true);
    setAdminMsg({ type: "", text: "" });
    try {
      const res = await api.platform.tiendas.addAdmin(slug, newAdmin);
      if (res.status === "success") {
        setAdminMsg({ type: "success", text: res.message || "Admin agregado." });
        setNewAdmin({ email: "", nombre: "", apellido: "", telefono: "" });
        await cargarAdmins();
      } else {
        setAdminMsg({ type: "error", text: res.message || "No se pudo agregar el admin." });
      }
    } catch {
      setAdminMsg({ type: "error", text: "Error de red o servidor." });
    }
    setAddingAdmin(false);
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const toggleCapacidad = (codigo, on) => setForm((f) => {
    const actuales = (f.capacidades || []).filter((c) => c !== codigo);
    return { ...f, capacidades: on ? [...actuales, codigo] : actuales };
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      // El template no viaja: toda tienda usa el 4 y lo pone el backend.
      const { template_id, template_codigo, template_nombre, catalogo_capacidades, ...payload } = form;
      const res = isEdit
        ? await api.platform.tiendas.update(slug, payload)
        : await api.platform.tiendas.create(payload);
      if (res.status === "success") {
        navigate("/platform");
      } else {
        setError(res.message || "No se pudo guardar.");
      }
    } catch {
      setError("Error de red o servidor.");
    }
    setSaving(false);
  };

  if (loading) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 48 }}><Spin size="large" /></div>;
  }

  return (
    <div style={{ maxWidth: 820, margin: "0 auto" }}>
      <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate("/platform")} style={{ marginBottom: 8 }}>
        Volver
      </Button>
      <Title level={4} style={{ marginBottom: 24 }}>
        {isEdit ? `Editar tienda: ${slug}` : "Nueva tienda"}
      </Title>

      <Card style={{ borderRadius: 12 }}>
        <form onSubmit={handleSubmit}>
          <Text strong style={{ display: "block", marginBottom: 12 }}>General</Text>
          <TwoCol>
            <div>
              <label style={{ fontSize: 13, color: '#666' }}>Slug (URL) *</label>
              <Input value={form.slug} onChange={set("slug")} disabled={isEdit} placeholder="Ej: avellanedafiltros" style={{ marginTop: 4 }} required />
              {isEdit && <Text type="secondary" style={{ fontSize: 11 }}>El slug no se puede cambiar</Text>}
            </div>
            <div>
              <label style={{ fontSize: 13, color: '#666' }}>Nombre *</label>
              <Input value={form.nombre} onChange={set("nombre")} style={{ marginTop: 4 }} required />
            </div>
            <div>
              <label style={{ fontSize: 13, color: '#666' }}>Nombre visible</label>
              <Input value={form.display_name} onChange={set("display_name")} style={{ marginTop: 4 }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 20 }}>
              <Switch checked={form.activa} onChange={(v) => setForm(f => ({ ...f, activa: v }))} />
              <Text>Activa</Text>
            </div>
          </TwoCol>
          <div style={{ marginTop: 16 }}>
            <label style={{ fontSize: 13, color: '#666' }}>Descripción</label>
            <Input.TextArea value={form.descripcion} onChange={set("descripcion")} minRows={2} style={{ marginTop: 4 }} />
          </div>

          <Divider />

          <Text strong style={{ display: "block", marginBottom: 12 }}>ERP</Text>
          <TwoCol>
            <div>
              <label style={{ fontSize: 13, color: '#666' }}>Tipo de ERP</label>
              <Select
                value={form.erp_type}
                onChange={(v) => setForm(f => ({ ...f, erp_type: v }))}
                options={ERP_TYPES.map(o => ({ value: o.value, label: o.label }))}
                style={{ width: '100%', marginTop: 4 }}
              />
            </div>
            {form.erp_type === "legacy_db" ? (
              <div>
                <label style={{ fontSize: 13, color: '#666' }}>Nombre BD legacy</label>
                <Input value={form.erp_db_name} onChange={set("erp_db_name")} style={{ marginTop: 4 }} />
              </div>
            ) : (
              <>
                <div>
                  <label style={{ fontSize: 13, color: '#666' }}>API Key ERP</label>
                  <Input value={form.erp_api_key} onChange={set("erp_api_key")} style={{ marginTop: 4 }} />
                </div>
                <div>
                  <label style={{ fontSize: 13, color: '#666' }}>Webhook secret</label>
                  <Input value={form.webhook_secret} onChange={set("webhook_secret")} style={{ marginTop: 4 }} />
                </div>
              </>
            )}
          </TwoCol>

          <Divider />

          <Text strong style={{ display: "block", marginBottom: 12 }}>Tema (colores)</Text>
          <ThreeCol>
            {[
              ["color_primary", "Primario"],
              ["color_secondary", "Secundario"],
              ["color_accent", "Acento"],
              ["color_background", "Fondo"],
              ["color_text", "Texto"],
              ["color_primary_text", "Texto sobre primario"],
            ].map(([key, label]) => (
              <div key={key}>
                <label style={{ fontSize: 13, color: '#666' }}>{label}</label>
                <Input value={form[key]} onChange={set(key)} placeholder="#000000" style={{ marginTop: 4 }} />
              </div>
            ))}
          </ThreeCol>

          <Divider />

          <Text strong style={{ display: "block", marginBottom: 12 }}>Contacto y redes</Text>
          <TwoCol>
            {[
              ["email", "Email"], ["telefono", "Teléfono"],
              ["whatsapp", "WhatsApp"], ["domicilio", "Domicilio"],
              ["website_url", "Website"], ["instagram", "Instagram"],
              ["facebook", "Facebook"],
            ].map(([key, label]) => (
              <div key={key}>
                <label style={{ fontSize: 13, color: '#666' }}>{label}</label>
                <Input value={form[key]} onChange={set(key)} style={{ marginTop: 4 }} />
              </div>
            ))}
          </TwoCol>

          <Divider />

          <Text strong style={{ display: "block", marginBottom: 4 }}>Dominio custom</Text>
          <Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 12 }}>
            Si la tienda tiene su propio dominio (ej: www.mitienda.com), ingresalo acá. El dueño del dominio debe apuntar un CNAME a tu servidor.
          </Text>
          <div style={{ maxWidth: 400 }}>
            <label style={{ fontSize: 13, color: '#666' }}>Dominio</label>
            <Input
              value={form.custom_domain || ""}
              onChange={set("custom_domain")}
              placeholder="www.mitienda.com"
              style={{ marginTop: 4 }}
            />
          </div>

          {/* Capacidades: funciones opcionales de la tienda. Se guardan con el
              resto del formulario; al crear todavía no hay tienda a la que prenderlas. */}
          {isEdit && (
            <>
              <Divider />
              <Text strong style={{ display: "block", marginBottom: 4 }}>Capacidades</Text>
              <Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 13 }}>
                Funciones opcionales. Apagada, no aparece en el panel de la tienda.
              </Text>
              {(form.catalogo_capacidades || []).map((cap, i) => {
                const on = (form.capacidades || []).includes(cap.codigo);
                return (
                  <div key={cap.codigo} style={{ padding: "10px 0", borderTop: i === 0 ? "none" : "1px solid #f0f0f0" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong style={{ fontSize: 14 }}>{cap.nombre}</Text>
                        <div><Text type="secondary" style={{ fontSize: 12 }}>{cap.descripcion}</Text></div>
                      </div>
                      <Switch checked={on} onChange={(v) => toggleCapacidad(cap.codigo, v)} aria-label={cap.nombre} />
                    </div>
                    {cap.codigo === "imagenes_serpapi" && on && (
                      <div style={{ maxWidth: 400, marginTop: 8 }}>
                        <label style={{ fontSize: 13, color: '#666' }}>API key de SerpAPI</label>
                        <Input.Password value={form.serpapi_key || ""} onChange={set("serpapi_key")} placeholder="Ingresá la API key de SerpAPI" style={{ marginTop: 4 }} autoComplete="new-password" />
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {error && <Alert type="error" message={error} style={{ marginTop: 16 }} />}

          <Space style={{ marginTop: 24 }}>
            <Button type="primary" htmlType="submit" loading={saving} style={{ background: "#0f172a", borderColor: "#0f172a" }}>
              {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear tienda"}
            </Button>
            <Button onClick={() => navigate("/platform")}>Cancelar</Button>
          </Space>
        </form>
      </Card>

      {isEdit && (
        <Card style={{ borderRadius: 12, marginTop: 24 }}>
          <Text strong style={{ display: "block", marginBottom: 4 }}>Administradores de la tienda</Text>
          <Text type="secondary" style={{ display: "block", marginBottom: 16, fontSize: 13 }}>
            Usuarios rol 1 que pueden entrar a /{slug}/admin. Acceden con login por código (OTP) a su email.
          </Text>

          <div style={{ marginBottom: 16 }}>
            {admins.length === 0 ? (
              <Text type="secondary">Todavía no hay administradores cargados.</Text>
            ) : (
              admins.map((a) => (
                <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <div>
                    <Text strong>{a.usuario}</Text>
                    <div><Text type="secondary" style={{ fontSize: 12 }}>{[a.nombre, a.apellido].filter(Boolean).join(" ") || "— sin nombre —"}</Text></div>
                  </div>
                  <Tag color="blue">Admin</Tag>
                </div>
              ))
            )}
          </div>

          <Divider />

          <form onSubmit={handleAddAdmin}>
            <Text strong style={{ display: "block", marginBottom: 12, fontSize: 14 }}>Agregar administrador</Text>
            <TwoCol>
              <div>
                <label style={{ fontSize: 13, color: '#666' }}>Email *</label>
                <Input
                  type="email" required value={newAdmin.email}
                  onChange={(e) => setNewAdmin(a => ({ ...a, email: e.target.value }))}
                  style={{ marginTop: 4 }} size="small"
                />
              </div>
              <div>
                <label style={{ fontSize: 13, color: '#666' }}>Teléfono (opcional)</label>
                <Input
                  value={newAdmin.telefono}
                  onChange={(e) => setNewAdmin(a => ({ ...a, telefono: e.target.value }))}
                  style={{ marginTop: 4 }} size="small"
                />
              </div>
              <div>
                <label style={{ fontSize: 13, color: '#666' }}>Nombre (opcional)</label>
                <Input
                  value={newAdmin.nombre}
                  onChange={(e) => setNewAdmin(a => ({ ...a, nombre: e.target.value }))}
                  style={{ marginTop: 4 }} size="small"
                />
              </div>
              <div>
                <label style={{ fontSize: 13, color: '#666' }}>Apellido (opcional)</label>
                <Input
                  value={newAdmin.apellido}
                  onChange={(e) => setNewAdmin(a => ({ ...a, apellido: e.target.value }))}
                  style={{ marginTop: 4 }} size="small"
                />
              </div>
            </TwoCol>
            {adminMsg.text && (
              <Text style={{ color: adminMsg.type === "error" ? "red" : "green", display: "block", marginTop: 12, fontSize: 13 }}>
                {adminMsg.text}
              </Text>
            )}
            <Button
              type="primary" htmlType="submit" loading={addingAdmin} icon={<UserAddOutlined />}
              style={{ marginTop: 16, background: "#0f172a", borderColor: "#0f172a" }}
            >
              {addingAdmin ? "Agregando..." : "Agregar admin"}
            </Button>
          </form>
        </Card>
      )}

      {isEdit && <PasarelasTienda slug={slug} />}
    </div>
  );
};
